/* @flow */
import React from 'react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import RequireForAccess, { ReactAccessConsumer } from 'react-access';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Link } from 'react-router-dom';

import Loading from 'components/Loading/Loading';
import type { RoleList } from 'permissions/defs';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import styles from './TitleActionButtonRow.module.scss';

type SubActionSpec = {
  label: string,
  permissions: RoleList,
  icon?: string,
  onClick?: () => void,
  to?: string,
};

export type ActionButtonSpec = {
  label: string,
  icon?: string,
  position?: 'left' | 'right',
} & (
  | {
      permissions: RoleList,
      onClick?: () => void,
      to?: string,
    }
  | { subActions: SubActionSpec[] }
);

export type TitleActionButtonRowProps = {
  className?: string,
  buttons: ActionButtonSpec[],
  loading?: boolean,
};

type InjectedProps = TranslatorProps;

@translate()
@observer
export default class TitleActionButtonRow extends ComponentWithInjectedProps<
  TitleActionButtonRowProps,
  {},
  InjectedProps
> {
  @observable anchorElement: HTMLElement | void;

  handleMultiButtonClick = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    if (event.nativeEvent && event.nativeEvent.target) {
      this.anchorElement = event.nativeEvent.target;
    }
  };

  handleMenuClose = () => {
    this.anchorElement = undefined;
  };

  renderSimpleActionButton(
    key: string,
    label: string,
    icon: ?string,
    to: ?string,
    onClick: ?() => void
  ) {
    const { t } = this.props;

    let LinkComponent = ({ children }) => (
      <Link to={to} onClick={onClick}>
        {children}
      </Link>
    );

    if (!to) {
      LinkComponent = ({ children }) => (
        <a href="javascript:;" onClick={onClick}>
          {children}
        </a>
      );
    }

    return (
      <div key={key} className={styles.actionButton}>
        <LinkComponent>
          <Button variant="outlined" color="default">
            {icon && (
              <svg className="button-icon">
                <use xlinkHref={icon} />
              </svg>
            )}
            {t(label)}
          </Button>
        </LinkComponent>
      </div>
    );
  }

  renderMultiActionButton(
    key: string,
    buttonSpec: { label: string, icon?: string, subActions: SubActionSpec[] }
  ) {
    const { anchorElement } = this;

    return (
      <ReactAccessConsumer key={key}>
        {authorizeAccess => {
          const { t } = this.props;
          const visibleSubActions = buttonSpec.subActions.filter(subAction =>
            authorizeAccess(toJS(subAction.permissions))
          );

          if (visibleSubActions.length === 0) {
            return null;
          }

          // Collapse down to a plain button when only one sub-action is
          // accessible: a dropdown with a single option is redundant.
          if (visibleSubActions.length === 1) {
            const [subAction] = visibleSubActions;
            return this.renderSimpleActionButton(
              key,
              buttonSpec.label,
              buttonSpec.icon,
              subAction.to,
              subAction.onClick
            );
          }

          return (
            <React.Fragment>
              <div className={styles.actionButton}>
                <Button variant="outlined" color="default" onClick={this.handleMultiButtonClick}>
                  {buttonSpec.icon && (
                    <svg className="button-icon">
                      <use xlinkHref={buttonSpec.icon} />
                    </svg>
                  )}
                  {t(buttonSpec.label)}
                </Button>
              </div>

              <Menu
                className={styles.subActionsMenu}
                anchorEl={anchorElement}
                open={!!anchorElement}
                onClose={this.handleMenuClose}
                getContentAnchorEl={null}
                elevation={0}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                BackdropProps={{
                  invisible: false,
                }}
                PaperProps={{
                  component: 'nav',
                }}
              >
                {visibleSubActions.map((subAction, index) => {
                  let LinkComponent = ({ children }) => (
                    <Link to={subAction.to} onClick={subAction.onClick}>
                      {children}
                    </Link>
                  );

                  if (!subAction.to) {
                    LinkComponent = ({ children }) => (
                      <a href="javascript:;" onClick={subAction.onClick}>
                        {children}
                      </a>
                    );
                  }

                  return (
                    <MenuItem key={`subaction-${index}`}>
                      <LinkComponent>
                        <Button variant="contained" color="primary" onClick={this.handleMenuClose}>
                          {t(subAction.label)}
                        </Button>
                      </LinkComponent>
                    </MenuItem>
                  );
                })}
              </Menu>
            </React.Fragment>
          );
        }}
      </ReactAccessConsumer>
    );
  }

  render() {
    const { className, loading, buttons } = this.props;

    return (
      <div className={[className, styles.buttonsWrapper].filter(item => item).join(' ')}>
        {loading && <Loading variant="small" />}

        {buttons.map((buttonSpec, index) => {
          if ('subActions' in buttonSpec) {
            return this.renderMultiActionButton(`multi-action-button-${index}`, buttonSpec);
          }

          return (
            <RequireForAccess
              key={`action-button-${buttonSpec.label}`}
              permissions={toJS(buttonSpec.permissions)}
            >
              {this.renderSimpleActionButton(
                `action-button-${buttonSpec.label}`,
                buttonSpec.label,
                buttonSpec.icon,
                buttonSpec.to,
                buttonSpec.onClick
              )}
            </RequireForAccess>
          );
        })}
      </div>
    );
  }
}
